import { CdkOverlayOrigin, OverlayModule } from '@angular/cdk/overlay';
import { TextFieldModule } from '@angular/cdk/text-field';
import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, ElementRef, NgModule, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatAutocompleteModule, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatChipsModule } from '@angular/material/chips';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialog } from '@angular/material/dialog';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { Utilities } from '@app/common/utilities';
import { AlertTemplatesComponent } from '@app/components/alert-templates/alert-templates.component';
import { ConfirmDialogComponent } from '@app/components/confirm-dialog/confirm-dialog.component';
import { ChannelControlModule } from '@app/components/form-controls/channel-control/channel-control.component';
import { GalleryModule } from '@app/components/gallery/gallery.component';
import { HtmlEditorModule } from '@app/components/html-editor/html-editor.component';
import { WidgetPlaceholderModule } from '@app/components/widget-placeholder/widget-placeholder.component';
import { NgSubscribeModule } from '@app/directives/ng-subscribe.directive';
import { ChannelTermPage } from '@app/models/channel-term-page';
import { Page, PageType } from '@app/models/page';
import { PeoplePickerUser } from '@app/models/people-picker-user';
import { SimplifiedTerm } from '@app/models/simplified-term';
import { LinkModule } from '@app/directives/link.directive';
import { SafeModule } from '@app/pipes/safe.pipe';
import { ChannelService } from '@app/services/channel.service';
import { FeatureSettingsStoreService } from '@app/services/feature-settings-store.service';
import { LanguageTermService } from '@app/services/language-term.service';
import { PagesService } from '@app/services/pages.service';
import { UserService } from '@app/services/user.service';
import { BehaviorSubject, combineLatest, Observable, of, Subscription } from 'rxjs';
import { concatMap, debounceTime, distinctUntilChanged, filter, map, switchMap, tap } from 'rxjs/operators';

enum ImageAlternative {
  COMPUTER,
  GALLERY,
  URL
}

@Component({
  selector: 'app-add-edit-page',
  templateUrl: './add-edit-page.component.html',
  styleUrls: ['../add-edit-page/add-edit-page.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class AddEditPageComponent implements OnInit, OnDestroy {

  page$: Observable<Page>;
  existingPage: Page;
  subscription = new Subscription();
  pageEditorForm: FormGroup;
  isNewsPage: boolean;
  saveBtnDisabled: boolean;
  auditsEnabled = this.featureSettingsStoreService.state.auditsEnabled;
  enableFeaturedPages = this.featureSettingsStoreService.state.enableFeaturedPages;
  enableNotificationSubscriptions = this.featureSettingsStoreService.state.enableNotificationSubscriptions;
  imageVaultUrl = this.featureSettingsStoreService.state.imageVaultUrl;

  /** Channel picker variables */
  channels$: Observable<Array<SimplifiedTerm>>;
  languages$: Observable<Array<SimplifiedTerm>>;
  selectedChannels: Array<SimplifiedTerm>;
  removedChannel$ = new BehaviorSubject<SimplifiedTerm>(null);
  removedChannels = 0;
  channelMenuOpen: boolean;
  languageFilteringEnabled: boolean;
  _triggerRect: ClientRect;
  @ViewChild('overlayOrigin') trigger: CdkOverlayOrigin;

  /** People picker variables */
  foundUsers$: Observable<Array<PeoplePickerUser>>;
  searchingForUsers = false;
  noUsersFound = false;
  selectedOwner$: Observable<PeoplePickerUser>;
  searchString: string;
  OwnerInputControl: FormControl;
  @ViewChild('ownerInput') ownerInput: ElementRef<HTMLInputElement>;
  fetchedChannels: Array<SimplifiedTerm>;

  /** Image variables */
  isInPictureEditMode = false;
  galleryImages$: Observable<Array<string>>;
  originalImageUrl: string;
  currentlySelectedImageData: any;
  currentlySelectedImageFileContents: any;
  currentlySelectedImageType: ImageAlternative;
  hasSelectedImage = false;
  hasTrashedImage = false;
  clearGallerySelections = false;

  constructor(private channelService: ChannelService,
              private activatedRoute: ActivatedRoute,
              private userService: UserService,
              private pageService: PagesService,
              private featureSettingsStoreService: FeatureSettingsStoreService,
              private router: Router,
              private snackBar: MatSnackBar,
              private dialog: MatDialog,
              private cdr: ChangeDetectorRef,
              private languageTermService: LanguageTermService) {

    this.languageFilteringEnabled = this.featureSettingsStoreService.state.enableLanguageFilteringForWorkplace;
    this.pageEditorForm = new FormGroup({
      Title: new FormControl('', Validators.required),
      Content: new FormControl('', Validators.required),
      ChannelTermPages: new FormControl(null),
      OwnerUserUpnId: new FormControl(null),
      BannerImageUrl: new FormControl(null),
      BannerImageText: new FormControl(null),
      BannerImageAltText: new FormControl(null),
      PublishingDate: new FormControl(null),
      IsCommentsEnabled: new FormControl(false),
      IsFeaturedPage: new FormControl(false),
      LanguageTermId: new FormControl(),
    });

    if (this.enableNotificationSubscriptions) {
      this.pageEditorForm.addControl('Notify', new FormControl(false));
    }

    this.OwnerInputControl = new FormControl('');
  }

  /** Set owner from people picker selection */
  setOwner(event: MatAutocompleteSelectedEvent): void {
    let userIdentifier = (event.option.value as PeoplePickerUser).UPN;
    if (userIdentifier.indexOf('#EXT#') !== -1) {
      userIdentifier = (event.option.value as PeoplePickerUser).Mail;
    }
    this.pageEditorForm.get('OwnerUserUpnId').setValue(userIdentifier);
    // Clear input field
    this.ownerInput.nativeElement.value = '';
  }

  /** Remove owner from people picker */
  removeOwner(): void {
    this.pageEditorForm.get('OwnerUserUpnId').setValue(null);
  }

  /** When channelinput is focused */
  channelFocus() {
    this.channelMenuOpen = !this.channelMenuOpen;
  }

  removeChannel(channel: SimplifiedTerm): void {
    const index = this.selectedChannels.indexOf(channel, 0);
    if (index > -1) {
      this.selectedChannels.splice(index, 1);
    }
    this.mapChannelTermPages();
    this.removedChannel$.next(channel);
    this.removedChannels = 0; // reset error message
    this.cdr.detectChanges();
  }

  handleChange(event: SimplifiedTerm[]): void {
    this.selectedChannels = event;
    this.mapChannelTermPages();
    this.removedChannels = 0; // reset error message
  }

  mapChannelTermPages(): void {
    const channelTermPages = this.selectedChannels.map(sc => {
      if (this.existingPage) {
        return {
          PageId: this.existingPage.Id,
          ChannelTermId: sc.Guid
        } as ChannelTermPage;
      } else {
        return {
          ChannelTermId: sc.Guid
        } as ChannelTermPage;
      }
    });
    this.pageEditorForm.get('ChannelTermPages').setValue(channelTermPages);
  }

  handleImageUpload(imageInput: any) {
    this.clearGallerySelections = true;
    this.hasSelectedImage = true;
    this.hasTrashedImage = false;

    this.currentlySelectedImageType = ImageAlternative.COMPUTER;
    if (imageInput.target.files.length) {
      this.currentlySelectedImageData = (imageInput.target.files[0] as File);
      const reader = new FileReader();
      reader.readAsDataURL(this.currentlySelectedImageData);
      reader.onload = (_event) => {
        const imageContent = reader.result.toString();
        this.bannerImageUrl = imageContent;
        this.currentlySelectedImageFileContents = imageContent;
      };
    }
  }

  handleImageUrl() {
    this.clearGallerySelections = true;
    this.hasSelectedImage = true;
    this.hasTrashedImage = false;

    this.currentlySelectedImageData = this.bannerImageUrl;
    this.currentlySelectedImageType = ImageAlternative.URL;
  }

  previewImageGallery(image: string) {
    this.bannerImageUrl = image;
    this.clearGallerySelections = false;
  }

  resetImageGalleryPreview() {
    if (this.hasSelectedImage && this.currentlySelectedImageType === ImageAlternative.COMPUTER) {
      this.bannerImageUrl = this.currentlySelectedImageFileContents;
    } else if (this.hasTrashedImage) {
      this.bannerImageUrl = null;
    } else if (this.hasSelectedImage) {
      this.bannerImageUrl = this.currentlySelectedImageData;
    } else if (!this.hasSelectedImage) {
      this.bannerImageUrl = this.originalImageUrl;
    } else {
      this.bannerImageUrl = null;
    }
  }

  handleImageGalleryChange(images: Array<string>) {
    this.hasSelectedImage = images.length > 0;
    if (this.hasSelectedImage) {
      // We can only receive one image so pick the first element
      const galleryImage = images[0];

      this.bannerImageUrl = galleryImage;
      this.currentlySelectedImageData = galleryImage;
      this.currentlySelectedImageType = ImageAlternative.GALLERY;
      this.hasTrashedImage = false;
    }
  }

  editBannerImage() {
    this.isInPictureEditMode = true;
  }

  removeBannerImage() {
    this.bannerImageUrl = null;
    this.hasSelectedImage = false;
    this.hasTrashedImage = true;
    this.currentlySelectedImageData = null;
    this.currentlySelectedImageFileContents = null;
    this.isInPictureEditMode = true;
    this.clearGallerySelections = true;
  }

  private handleSave(val: Page, newPage: boolean): void {
    if (newPage) {
      this.router.navigate([`../${val.SlugTitle}-${val.Id}`], { relativeTo: this.activatedRoute });
      Utilities.openSnackBar(this.snackBar, AlertTemplatesComponent, AlertTemplatesComponent.genericadded);
    } else {
      this.router.navigate([`../../${val.SlugTitle}-${val.Id}`], { relativeTo: this.activatedRoute });
      Utilities.openSnackBar(this.snackBar, AlertTemplatesComponent, AlertTemplatesComponent.genericupdated);
    }
  }

  private handleError(error: any): void {
    if (error && error.error.errors) {
      Object.keys(error.error.errors).forEach(key => {
        const control = this.pageEditorForm.get(key);
        if (control) {
          control.setErrors({ key: error.error.errors[key][0] });
        }
      });
    }
    this.saveBtnDisabled = false;
  }

  get bannerImageUrl() {
    return this.pageEditorForm.get('BannerImageUrl').value;
  }

  set bannerImageUrl(value: string) {
    this.pageEditorForm.get('BannerImageUrl').setValue(value);
  }

  get bannerImageText() {
    return this.pageEditorForm.get('BannerImageText').value;
  }

  get bannerImageAltText() {
    return this.pageEditorForm.get('BannerImageAltText').value;
  }

  ngOnInit() {
    this.subscription.add(this.activatedRoute.url.subscribe(urlsegments => {
      this.isNewsPage = urlsegments[0].path === 'news';
    }));

    this.subscription.add(this.pageService.updatedPageFromAudit$.subscribe(updatedPage => {
      if (this.existingPage.Id === updatedPage.Id) {
        this.handleSave(updatedPage, false);
      }
    }));

    this.selectedOwner$ = this.pageEditorForm.get('OwnerUserUpnId')
      .valueChanges
      .pipe(
        switchMap(upnId => {
          return upnId ? this.userService.getUserByUpn(upnId) : of(null);
        }),
        map(user => {
          // map to PeoplePickerUser because we don't need all attributes
          return user ? {
            GraphId: user.id,
            Name: user.displayName,
            UPN: user.userPrincipalName,
            Mail: user.mail
          } as PeoplePickerUser : null;
        })
      );

    this.page$ = this.activatedRoute.data.pipe(
      map(data => {
        const page = data.page as Page;
        return page;
      }),
      tap(page => {
        if (page) {
          this.existingPage = page;
          // Fill the form with data from the fetched page
          Object.keys(page).forEach(prop => {
            if (this.pageEditorForm.contains(prop) && page[prop]) {
              if (prop !== 'Notify') {
                this.pageEditorForm.get(prop).patchValue(page[prop]);
              }
            }
          });
          // Save the original image URL so we can use previews / "selected but not yet saved" modes
          this.originalImageUrl = page.BannerImageUrl;
          this.currentlySelectedImageData = this.originalImageUrl;

          // Force angular to detect the changes
          this.cdr.detectChanges();
        }
      })
    );

    this.languages$ = combineLatest([this.languageTermService.getLanguages(), this.page$]).pipe(
      map(([fetchedLanguages, page]) => {
        if (page) {
          if (page.LanguageTermId) {

            // Get the selected simplifiedTerms based on the languageTermAppTiles from the apptile.
            const selectedLanguage = fetchedLanguages.filter(fl => fl.Guid === page.LanguageTermId);

            if (selectedLanguage[0]) {
              // set the selected values in the from
              this.pageEditorForm.patchValue({
                LanguageTermId: selectedLanguage[0].Guid
              });
            }
          }
        }
        console.log('Fetched languages: ', fetchedLanguages);
        return fetchedLanguages;
      }));

    this.channels$ = combineLatest([this.removedChannel$, this.channelService.getChannels(), this.page$])
      .pipe(
        map(([removedChannel, fetchedChannels, page]) => {
          if (removedChannel) {
            fetchedChannels.findRecursive(fc => fc.Guid === removedChannel.Guid, 'Terms').Selected = false;
          } else if (page) {
            if (page.ChannelTermPages && page.ChannelTermPages.length > 0) {
              this.selectedChannels = page.ChannelTermPages.map(ct => {
                const selectedChannel = fetchedChannels.findRecursive(fc => fc.Guid === ct.ChannelTermId, 'Terms');
                if (selectedChannel) {
                  selectedChannel.Selected = true;
                }
                return selectedChannel;
              });
              this.removedChannels = this.selectedChannels.filter(c => !c).length;
              if (this.removedChannels > 0) {
                this.selectedChannels = this.selectedChannels.filter(c => c);
              }
            }
          }
          this.fetchedChannels = fetchedChannels;
          return fetchedChannels;
        })
      );

    this.foundUsers$ = this.OwnerInputControl
      .valueChanges
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        filter(searchTerm => typeof searchTerm === 'string' && searchTerm.length > 0),
        switchMap(searchTerm => {
          this.noUsersFound = false;
          this.searchingForUsers = true;
          return this.userService.searchMsGraphForUsers(searchTerm, 5);
        }),
        map(userArray => {
          // map results and return
          return userArray.map(u => {
            return {
              GraphId: u.id,
              Name: u.displayName,
              UPN: u.userPrincipalName,
              Mail: u.mail
            } as PeoplePickerUser;
          });
        }),
        tap((users) => {
          this.searchingForUsers = false;
          if (users.length === 0) {
            this.noUsersFound = true;
          }
        })
      );

    this.galleryImages$ = this.pageService.getImages();
  }

  save() {
    if (this.pageEditorForm.valid) {
      this.saveBtnDisabled = true;
      const newOrUpdatedPage = this.pageEditorForm.value as Page;
      newOrUpdatedPage.PageType = this.isNewsPage ? PageType.News : PageType.Content;
      if (this.existingPage) {

        // If languagefiltering is disabled, don't change the language value
        if (!this.languageFilteringEnabled) {
          newOrUpdatedPage.LanguageTermId = this.existingPage.LanguageTermId;
        }

        newOrUpdatedPage.Id = this.existingPage.Id;
        if (this.currentlySelectedImageData && this.currentlySelectedImageType === ImageAlternative.COMPUTER) {
          this.pageService
            .addImage(this.currentlySelectedImageData).pipe(
              concatMap(url => {
                newOrUpdatedPage.BannerImageUrl = url;
                return this.pageService.updatePage(newOrUpdatedPage.Id, newOrUpdatedPage);
              })
            ).subscribe(
              (val) => this.handleSave(val, false),
              (err) => this.handleError(err)
            );
        } else {
          this.pageService
            .updatePage(newOrUpdatedPage.Id, newOrUpdatedPage).subscribe(
              (val) => this.handleSave(val, false),
              (err) => this.handleError(err)
            );
        }
      } else {
        if (this.currentlySelectedImageData && this.currentlySelectedImageType === ImageAlternative.COMPUTER) {
          this.subscription.add(
            this.pageService
              .addImage(this.currentlySelectedImageData).pipe(
                concatMap(url => {
                  newOrUpdatedPage.BannerImageUrl = url;
                  return this.pageService.addPage(newOrUpdatedPage);
                })
              ).subscribe(
                (val) => this.handleSave(val, true),
                (err) => this.handleError(err))
          );
        } else {
          this.subscription.add(
            this.pageService
              .addPage(newOrUpdatedPage).subscribe(
                (val) => this.handleSave(val, true),
                (err) => this.handleError(err))
          );
        }
      }
    }
  }

  delete() {
    if (this.existingPage) {
      const dialogRef = this.dialog.open(ConfirmDialogComponent);
      this.subscription.add(dialogRef.afterClosed().pipe(
        filter(result => result),
        concatMap(result =>
          this.pageService
            .deletePage(this.existingPage.Id)
        )).subscribe(
          (val) => {
            this.router.navigate(['../../'], { relativeTo: this.activatedRoute });
            Utilities.openSnackBar(this.snackBar, AlertTemplatesComponent, AlertTemplatesComponent.genericdeleted);
          },
          (err) => this.handleError(err))
      );
    }
  }

  removeAllChannels() {
    this.selectedChannels = [];
    const allTerms = this.fetchedChannels.filterRecursive(c => c.Selected, 'Terms');
    allTerms.forEach(ch => {
      this.removedChannel$.next(ch);
      this.cdr.detectChanges();
    });
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }
}
@NgModule({
  declarations: [
    AddEditPageComponent
  ],
  exports: [
    AddEditPageComponent
  ],
  imports: [
    CommonModule,
    LinkModule,
    RouterModule,
    MatSlideToggleModule,
    HtmlEditorModule,
    ReactiveFormsModule,
    SafeModule,
    MatFormFieldModule,
    MatInputModule,
    ChannelControlModule,
    MatButtonModule,
    OverlayModule,
    MatChipsModule,
    MatAutocompleteModule,
    MatProgressSpinnerModule,
    NgSubscribeModule,
    MatIconModule,
    MatNativeDateModule,
    MatDatepickerModule,
    MatExpansionModule,
    GalleryModule,
    WidgetPlaceholderModule,
    TextFieldModule,
    MatSelectModule
  ]
})
export class AddEditPageModule { }
